import { AccountApi, AuthnApi, SocialIdpApi, SsoIdpApi, UserApi, UsersApiV2 } from 'common/api';
import UserAccountApi from 'common/api/services/UserAccountApi';
import { SOCIAL_IDP_TYPE, SOCIAL_IDP_TYPE_NAME } from 'common/components/SocialIdpCertWindow';
import {
  ACCOUNT_TYPE,
  APP_STORE_SIGN_ON_MODE,
  CREDENTIAL_ERROR,
  IDP_TYPE,
  USER_VERIFY_CODE_SCENE,
  VENDOR_TYPE,
  WECOM_BIND_STATUS,
} from 'common/constants/Constants';
import Account from 'common/http/servicesV2/Account';
import UsersApi from 'common/http/servicesV2/UsersApi';
import i18n from 'common/locales';
import Utils from 'common/utils';
import JSRuntime from 'common/utils/JSRuntime';
import { AppStoreApi, PersonalEventsApi, PwdMatchedPolicyApi } from 'idpBase/api';
import { TGlobalStore, TUpdateStore } from 'idpBase/store';
import { DEFAULT_LOG_QUERY } from './constants/Constants';
class ProfileStore {
  public readonly global: TGlobalStore;
  public updateStore: TUpdateStore<ProfileStore>;
  public resetStore: () => void;

  public isPersonalLoading = true;
  public isBlockLoading = true;
  public settings = { system: { smsEnabled: true } }; // 有登录态之后的 settings
  public features = { hasPortal: true, landingPage: '/' }; // 有登录态之后的 settings
  public weComInfo = {
    ssoTenantId: '',
    idpType: SOCIAL_IDP_TYPE.WECOM,
    status: WECOM_BIND_STATUS.DISABLED, // status: 1: 已绑定，0: 未绑定
  };

  public expire = { isExpire: false, expiredDate: '' };
  public showExpireTip = sessionStorage.getItem('showExpireTip') || 'true';
  public policy = {};
  public isPolicyLoading = false;

  public logQuery = DEFAULT_LOG_QUERY;
  public userLogs = {
    total: 0,
    list: [],
  };
  public isLogsLoading = false;
  public appStore = {};

  public get isPersonal() {
    const accountType = m.get(this.global.rootStore.userInfo, 'account.accountType');
    return accountType === ACCOUNT_TYPE.PERSONAL;
  }

  /** 私有化无法进账号管理页面，直接返回个人中心页面 */
  public get showAccountManageEntrance() {
    return !JSRuntime.isPrivate;
  }

  public init() {
    return Promise.all([
      this.getWeComBindInfo(),
      this.getSettings(),
      this.checkExpired(),
      this.getAppStore(),
    ]);
  }

  public getSettings() {
    return AccountApi.getTenantSettings()
      .then((settings) => {
        const smsEnabled = _.get(settings, 'system.smsEnabled');
        const features = _.get(settings, 'account.features', []);
        const featureMap = { ...this.features };
        features.forEach((feature: { code: string }) => {
          const code = _.get(feature, 'code') || '';
          const extras = _.get(feature, 'extras') || '';
          if (_.startsWith(code, 'menu:') || !extras || !_.isString(extras)) return;

          const props = Utils.safeParseJSON(extras);
          Object.assign(featureMap, props);
        });
        this.updateStore({
          features: featureMap,
          settings: { system: { smsEnabled } },
        });
      })
      .catch((err) => {
        this.global.handleError(err);
      });
  }

  // 仅用来校验及获得 avatarURL 地址
  public uploadAvatar(file: File, originalUrl?: string, onStatusChange?: (url: string) => void) {
    return Account.uploadAvatar({
      img: file,
      originalUrl,
      onStatusChange,
      uploadResource: 'account_user_logo',
    });
  }

  // 获取企微绑定信息
  public getWeComBindInfo() {
    return AccountApi.getWeComBindInfo(IDP_TYPE.WeCom)
      .then((weComInfo) => {
        this.updateStore({ weComInfo });
      })
      .catch((error) => {
        this.global.handleError(error);
      });
  }

  // 给账户变更 avatarUrl
  public setAvatarUrl(avatarUrl: string) {
    return UsersApi.saveAvatar(avatarUrl)
      .then(() => {
        this.global.rootStore.getUserInfo();
      })
      .catch(() => {
        this.global.handleError(i18n.t('更新失败，请稍后重试', { ns: 'idp' }));
        return Promise.reject();
      });
  }

  public sendCode(event?: USER_VERIFY_CODE_SCENE, mobile?: string) {
    return UserApi.sendCode(event, mobile);
  }

  public verifyMobile(
    verificationCode: string,
    verificationSessionId: string,
    event?: USER_VERIFY_CODE_SCENE,
  ) {
    return UserApi.verifyMobile({
      verificationCode,
      verificationSessionId,
      event,
    });
  }

  // 自然人二次验证获取验证码
  public natureSendCode() {
    return AuthnApi.getSMS();
  }

  // 自然人二次验证验证码-会切换登录态
  public natureVerifyMobile(
    verificationCode: string,
    verificationSessionId: string,
    accountId: string,
  ) {
    return AuthnApi.verifyCode({
      verificationCode,
      verificationSessionId,
      accountId,
    });
  }

  public setMobile(data: { verificationCode: string; verificationSessionId: string }) {
    return UserApi.setMobile(data);
  }

  public async verifyOldPwd(password: string) {
    const credential = await this.global.rootStore.encryptRequest({ password });
    return credential
      ? UserApi.verifyOldPassword(credential)
      : Promise.reject({ data: { errCode: CREDENTIAL_ERROR } });
  }

  public async verifyEnterpriseOldPwd(password: string) {
    const credential = await this.global.rootStore.encryptRequest({ password });
    return credential
      ? UserApi.verifyEnterpriseOldPwd(credential)
      : Promise.reject({ data: { errCode: CREDENTIAL_ERROR } });
  }

  public async setOrResetPassword(password: string) {
    // 外部处理 catch 逻辑
    const credential = await this.global.rootStore.encryptRequest({ password });
    return credential
      ? UserApi.setOrResetPassword(credential)
      : Promise.reject({ data: { errCode: CREDENTIAL_ERROR } });
  }

  public async setOrResetEnterprisePwd(password: string) {
    const credential = await this.global.rootStore.encryptRequest({ password });
    return credential
      ? UserApi.setOrResetEnterprisePwd(credential)
      : Promise.reject({ data: { errCode: CREDENTIAL_ERROR } });
  }

  // 获取社会认证源信息 微信、QQ
  public getSocialIdpData(idpType: SOCIAL_IDP_TYPE) {
    // 企业微信的单独接口
    const promise =
      idpType === SOCIAL_IDP_TYPE.WECOM
        ? AuthnApi.getWeComInfo(SOCIAL_IDP_TYPE.WECOM)
        : AuthnApi.getSocialIdp(idpType);

    return promise.catch((err) => {
      this.global.handleError(err);
      return Promise.reject(err);
    });
  }

  public unbindSocialIdp(idpType: SOCIAL_IDP_TYPE) {
    // 企业微信的单独接口
    const promise =
      idpType === SOCIAL_IDP_TYPE.WECOM
        ? SsoIdpApi.unbindUser(SOCIAL_IDP_TYPE.WECOM)
        : SocialIdpApi.unbindSocialIdp(idpType);
    return promise.then(() => {
      this.global.rootStore.getUserInfo();
      this.getWeComBindInfo();
    });
    // 外部处理error
  }

  public bindSocialIdp(
    idpType: SOCIAL_IDP_TYPE,
    body: { state: string; code: string; redirectUri?: string },
  ) {
    // 企业微信的单独接口
    const promise =
      idpType === SOCIAL_IDP_TYPE.WECOM
        ? SsoIdpApi.bindUser(SOCIAL_IDP_TYPE.WECOM, _.pick(body, 'state', 'code'))
        : SocialIdpApi.bindSocialIdp(idpType, body);

    return promise.then(() => {
      this.global.handleSuccess(
        i18n.t('{type}绑定成功', {
          ns: 'idp',
          type: SOCIAL_IDP_TYPE_NAME[idpType],
        }),
      );
      this.global.rootStore.getUserInfo();
      this.getWeComBindInfo();
    });
    // catch 交给外部处理
  }

  public checkExpired() {
    PwdMatchedPolicyApi.checkExpired()
      .then((expireData) => {
        // validResult为 true 表示需要提示
        if (_.get(expireData, 'validResult') === true) {
          this.updateStore({
            expire: {
              isExpire: true,
              expiredDate: _.get(expireData, 'expiredDate'),
            },
          });
        } else {
          this.updateStore({
            expire: {
              isExpire: false,
              expiredDate: '',
            },
          });
        }
      })
      .catch((err) => {
        this.global.handleError(err);
      });
  }

  // 获取密码策略
  public getPwdPolicy() {
    this.isPolicyLoading = true;
    UserAccountApi.getPasswordPolicy()
      .then((policy) => {
        this.updateStore({ policy: policy || {}, isPolicyLoading: false });
      })
      .catch((err) => {
        this.global.handleError(err);
        this.updateStore({ isPolicyLoading: false });
      });
  }

  // 更新个人版租户名称
  public updatePersonalName(bodyData: { name: string }) {
    return UsersApiV2.updatePersonalName(bodyData);
  }

  public getAppStore() {
    return AppStoreApi.getByParams({
      pageNumber: 1,
      pageSize: 30,
      signOnMode: APP_STORE_SIGN_ON_MODE.OIDC,
      vendorType: VENDOR_TYPE.Internal,
    })
      .then((res) => {
        const curAppStore = m.get(res, 'appStore.0') || {};
        this.updateStore({
          appStore: curAppStore,
        });
        if (m.get(curAppStore, 'settings.enable_privilege_grant')) {
          this.getUserLogs();
        }
      })
      .catch((error) => {
        this.global.handleError(error);
      });
  }

  // 获取用户日志
  public getUserLogs(params?: Record<string, any>) {
    this.isLogsLoading = true;
    const query = {
      ...this.logQuery,
      ...params,
    };
    PersonalEventsApi.getByParams(query)
      .then((res) => {
        this.updateStore({
          userLogs: res,
          logQuery: query,
        });
      })
      .catch((err) => {
        this.global.handleError(err);
      })
      .finally(() => {
        this.updateStore({
          isLogsLoading: false,
        });
      });
  }
}

export default ProfileStore;
